import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Logs, Loader, Edit, RefreshCw, ChevronRight, ChevronLeft, FileText, Search, X, PlugZap, FolderClock, FolderSync, FolderSymlink, Files, Link2, Menu, Check, CalendarClock, Sparkles } from "lucide-react";
import type { Source, Job, WorkspaceFile, SyncSchedule, BrowseFolder } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import SourceBrowser from '../components/SourceBrowser';
import SourceReviewTab from '../components/SourceReviewTab';
import SourceQueriesTab from '../components/SourceQueriesTab';
import FileRow from '../components/FileRow';
import FolderRow from '../components/FolderRow';
import DismissableInfoCard from '../components/DismissableInfoCard';
import { MetadataInfo } from '../lib/files';
import SourceForm from '../components/SourceForm';
import { formatDateTime, titleCaseSnake } from '../lib/text';
import { JobStatusIcon, jobStatusBgColor } from '../lib/jobs';
import { runOAuthPopup } from '../lib/popup';
import { AUTH_PROVIDERS } from '../lib/authProviders';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import Tabs from '../components/Tabs';
import SourceLogo from '../components/SourceLogo';
import { isBuiltinSource, sourceLabel } from '../lib/sources';

type TabType = 'files' | 'jobs' | 'review' | 'searches';
const VALID_TABS: TabType[] = ['files', 'jobs', 'review', 'searches'];


const SYNC_SCHEDULES: { value: SyncSchedule; label: string }[] = [
    { value: 'off', label: 'Off' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
];

const MENU_ITEM = 'w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-graphite hover:bg-offwhite disabled:opacity-50 disabled:cursor-not-allowed';

function SourceActionsMenu({
    isEditing, onEdit, onReconnect, reconnecting, schedule, onScheduleChange, onSyncNow, syncing, syncs,
}: {
    isEditing: boolean;
    onEdit: () => void;
    onReconnect?: () => void;
    reconnecting: boolean;
    schedule: SyncSchedule;
    onScheduleChange: (schedule: SyncSchedule) => void;
    onSyncNow: () => void;
    syncing: boolean;
    // False for a source searched live, which has no schedule and nothing to sync.
    syncs: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => { if (!open) setScheduleOpen(false); }, [open]);

    const run = (action: () => void) => () => { setOpen(false); action(); };
    const current = SYNC_SCHEDULES.find(s => s.value === schedule)?.label ?? 'Off';
    const busy = syncing || reconnecting;

    return (
        <div ref={ref} className="relative shrink-0">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-label="Source actions"
                aria-haspopup="menu"
                aria-expanded={open}
                title="Source actions"
                className={`w-10 h-10 grid place-items-center border border-softgrey rounded-lg text-graphite transition-colors ${open ? 'bg-offwhite' : 'hover:bg-offwhite'}`}
            >
                {busy ? <Loader className="w-5 h-5 animate-spin text-brand" /> : <Menu className="w-5 h-5" />}
            </button>

            {open && (
                <div role="menu" className="absolute top-full right-0 mt-2 w-56 navaris-dropdown z-30">
                    <button type="button" role="menuitem" onClick={run(onEdit)} className={MENU_ITEM}>
                        <Edit className="w-4 h-4 shrink-0 text-coolslate" />
                        {isEditing ? 'Cancel editing' : 'Edit'}
                    </button>
                    {onReconnect && (
                        <button type="button" role="menuitem" onClick={run(onReconnect)} disabled={reconnecting} className={MENU_ITEM}>
                            <PlugZap className="w-4 h-4 shrink-0 text-coolslate" />
                            {reconnecting ? 'Connecting...' : 'Reconnect'}
                        </button>
                    )}

                    {syncs && <>
                    <div className="relative" onMouseEnter={() => setScheduleOpen(true)} onMouseLeave={() => setScheduleOpen(false)}>
                        <button
                            type="button"
                            role="menuitem"
                            aria-haspopup="menu"
                            aria-expanded={scheduleOpen}
                            onClick={() => setScheduleOpen(o => !o)}
                            className={`${MENU_ITEM} ${scheduleOpen ? 'bg-offwhite' : ''}`}
                        >
                            <ChevronLeft className="w-4 h-4 shrink-0 text-coolslate" />
                            <CalendarClock className="w-4 h-4 shrink-0 text-coolslate" />
                            Sync schedule
                            <span className="ml-auto text-xs text-coolslate">{current}</span>
                        </button>
                        {scheduleOpen && (
                            <div className="absolute top-0 right-full pr-1">
                                <div role="menu" className="w-40 navaris-dropdown">
                                    {SYNC_SCHEDULES.map(s => (
                                        <button
                                            key={s.value}
                                            type="button"
                                            role="menuitemradio"
                                            aria-checked={s.value === schedule}
                                            onClick={run(() => { if (s.value !== schedule) onScheduleChange(s.value); })}
                                            className={MENU_ITEM}
                                        >
                                            <span className="w-4 h-4 shrink-0 grid place-items-center">
                                                {s.value === schedule && <Check className="w-4 h-4 text-brand-dark" />}
                                            </span>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="my-1 border-t border-softgrey/60" />
                    <button type="button" role="menuitem" onClick={run(onSyncNow)} disabled={syncing} className={MENU_ITEM}>
                        <RefreshCw className={`w-4 h-4 shrink-0 text-coolslate ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Sync now'}
                    </button>
                    </>}
                </div>
            )}
        </div>
    );
}

export default function DataSourceDetailPage() {
    const { authFetch } = useAuth();
    const { hasActiveJobs } = useWorkspace();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    // The URL is the source of truth, so back/forward and external links land on the tab they encode.
    const activeTab: TabType = tabParam && VALID_TABS.includes(tabParam as TabType)
        ? (tabParam as TabType)
        : 'files';
    const setActiveTab = useCallback((tab: TabType) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 'files') next.delete('tab'); else next.set('tab', tab);
            if (tab !== 'files') next.delete('folder');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const [source, setSource] = useState<Source | null>(null);
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshingSource, setRefreshingSource] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<{ folders: BrowseFolder[]; files: WorkspaceFile[] }>({ folders: [], files: [] });
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPage, setSearchPage] = useState(1);
    const [searchTotal, setSearchTotal] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
                            // pr-1 bridges the gap to the menu so the pointer can cross it.
    const pathParts = location.pathname.split('/').filter(p => p);
    const sourceId = pathParts[1];

    const folderParam = searchParams.get('folder');
    const initialFolder = folderParam ? folderParam.split('/') : [];

    // `type` carries the doc type being linked for, so the file and folder pages can offer it as a candidate.
    const typeParam = searchParams.get('type');
    const linkQuery = typeParam ? `?type=${encodeURIComponent(typeParam)}` : undefined;

    const handleFolderChange = useCallback((folder: string[]) => {
        const params: Record<string, string> = { tab: activeTab };
        if (folder.length > 0) {
            params.folder = folder.join('/');
        }
        if (typeParam) params.type = typeParam;
        setSearchParams(params);
    }, [activeTab, typeParam, setSearchParams]);

    const loadSourceDetails = useCallback(async (silent = false) => {
        if (!sourceId) return;
        if (!silent) setLoading(true);
        setError(null);
        try {
            const sourceRes = await authFetch(`/sources/${sourceId}`);
            if (!sourceRes.ok) throw new Error('Failed to load connection');
            setSource(await sourceRes.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load connection');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [sourceId, authFetch]);

    useEffect(() => { loadSourceDetails(); }, [loadSourceDetails]);

    // Reload when `hasActiveJobs` flips, so the Jobs tab catches the post-completion status.
    const loadJobs = useCallback(async (): Promise<Job[]> => {
        if (!sourceId) return [];
        try {
            const res = await authFetch(`/jobs?source_id=${encodeURIComponent(sourceId)}`);
            if (res.ok) {
                const mine: Job[] = await res.json();
                setJobs(mine);
                return mine;
            }
        } catch {}
        return [];
    }, [sourceId, authFetch]);

    useEffect(() => {
        loadJobs().finally(() => setLoadingJobs(false));
    }, [loadJobs, hasActiveJobs]);

    const isSearchActive = searchQuery.trim().length >= 3;

    useEffect(() => {
        if (!isSearchActive || !sourceId) {
            setSearchResults({ folders: [], files: [] });
            setSearchPage(1);
            setSearchTotal(0);
            return;
        }

        const controller = new AbortController();
        const timeout = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await authFetch(
                    `/sources/${sourceId}/files?search=${encodeURIComponent(searchQuery.trim())}&page=1`,
                    { signal: controller.signal }
                );
                if (res.ok) {
                    const data = await res.json();
                    setSearchResults({ folders: data.folders ?? [], files: data.files });
                    setSearchTotal(data.total);
                    setSearchPage(1);
                }
            } catch {
            } finally {
                setSearchLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [searchQuery, sourceId, isSearchActive]);

    const loadNextPage = useCallback(async () => {
        if (!isSearchActive || !sourceId || loadingMore) return;
        const nextPage = searchPage + 1;
        setLoadingMore(true);
        try {
            const res = await authFetch(
                `/sources/${sourceId}/files?search=${encodeURIComponent(searchQuery.trim())}&page=${nextPage}`
            );
            if (res.ok) {
                const data = await res.json();
                setSearchResults(prev => ({ folders: prev.folders, files: [...prev.files, ...data.files] }));
                setSearchPage(nextPage);
            }
        } catch {
        } finally {
            setLoadingMore(false);
        }
    }, [isSearchActive, sourceId, searchQuery, searchPage, loadingMore]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        const hasMore = searchResults.files.length < searchTotal;
        if (!hasMore || searchLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadNextPage();
                }
            },
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [searchResults.files.length, searchTotal, searchLoading, loadNextPage]);

    type FolderEntry = { id: string; name: string; linked?: boolean; linkedCount?: number };
    const [browsePath, setBrowsePath] = useState<FolderEntry[]>(
        () => initialFolder.map(name => ({ id: name, name }))
    );
    const [browseFolders, setBrowseFolders] = useState<FolderEntry[]>([]);
    const [browseFiles, setBrowseFiles] = useState<WorkspaceFile[]>([]);
    const [browseLoading, setBrowseLoading] = useState(true);
    const [browseError, setBrowseError] = useState<string | null>(null);
    const [currentFolderLinked, setCurrentFolderLinked] = useState(false);

    // The ?folder= param is the source of truth for the browse path; equality-guarded to skip a redundant refetch.
    useEffect(() => {
        const desired = folderParam ? folderParam.split('/') : [];
        setBrowsePath(prev => {
            const current = prev.map(f => f.name);
            if (current.length === desired.length && current.every((n, i) => n === desired[i])) {
                return prev;
            }
            return desired.map(name => ({ id: name, name }));
        });
    }, [folderParam]);

    const loadBrowseFolder = useCallback(async (silent = false): Promise<void> => {
        if (!sourceId) return;
        if (!silent) {
            setBrowseLoading(true);
            setBrowseError(null);
        }
        const directory = browsePath.map(f => f.name).join('/');
        const params = directory ? `?directory=${encodeURIComponent(directory)}` : '';
        try {
            const res = await authFetch(`/sources/${sourceId}/files${params}`);
            if (!res.ok) throw new Error('Failed to load files');
            const data = await res.json();
            setBrowseFolders(
                (data.folders as BrowseFolder[])
                    .map(f => ({ id: f.name, name: f.name, linked: f.linked, linkedCount: f.linked_count }))
            );
            setBrowseFiles(data.files as WorkspaceFile[]);
            setCurrentFolderLinked(!!data.current_folder_linked);
        } catch (e) {
            if (!silent) setBrowseError(e instanceof Error ? e.message : 'Failed to load files');
        } finally {
            if (!silent) setBrowseLoading(false);
        }
    }, [sourceId, browsePath, authFetch]);

    useEffect(() => { loadBrowseFolder(); }, [loadBrowseFolder]);

    // Poll while the overview reports activity or this page has an in-flight job, so Sync Now starts polling at once.
    const anyLocalJobInFlight = jobs.some(j =>
        j.status === 'pending' || j.status === 'queued' || j.status === 'processing'
    );
    useEffect(() => {
        if (!sourceId || (!hasActiveJobs && !anyLocalJobInFlight)) return;
        const interval = setInterval(async () => {
            const mine = await loadJobs();
            const anyInFlight = mine.some(j =>
                j.status === 'pending' || j.status === 'queued' || j.status === 'processing'
            );
            if (anyInFlight) {
                loadSourceDetails(true);
                loadBrowseFolder(true);
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [sourceId, hasActiveJobs, anyLocalJobInFlight, loadSourceDetails, loadJobs, loadBrowseFolder]);

    const handleNavigate = useCallback((newPath: FolderEntry[]) => {
        handleFolderChange(newPath.map(f => f.name));
    }, [handleFolderChange]);

    const handleScheduleChange = async (schedule: SyncSchedule) => {
        if (!sourceId) return;
        try {
            const res = await authFetch(`/sources/${sourceId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sync_schedule: schedule }),
            });
            if (!res.ok) throw new Error('Failed to update schedule');
            const updated = await res.json();
            setSource(updated);
        } catch (err) {
            console.error('Failed to update sync schedule:', err);
            alert('Failed to update sync schedule. Please try again.');
        }
    };

    const handleRefreshSource = async () => {
        if (!sourceId) return;

        if (!window.confirm('Are you sure you want to refresh this connection? This will re-ingest all content.')) {
            return;
        }

        setRefreshingSource(true);
        try {
            const response = await authFetch(`/sources/${sourceId}/refresh`, {
                method: 'POST',
            });

            if (!response.ok) throw new Error('Failed to refresh connection');

            await response.json();

            // Pull the just-queued job into the local list so the poller starts immediately.
            setActiveTab('jobs');
            await loadJobs();
        } catch (err) {
            console.error('Failed to refresh source:', err);
            alert('Failed to refresh the connection. Please try again.');
        } finally {
            setRefreshingSource(false);
        }
    };

    // Reconnect is for the file/mail providers whose credentials expire; paste-key sources re-authorise via the connect form.
    const OAUTH_SOURCES = ['onedrive', 'sharepoint', 'drive', 'gmail', 'dropbox', 'box', 'notion', 'slack', 'teams'];
    const isOAuthSource = source ? OAUTH_SOURCES.includes(source.source_type) : false;

    const [reconnecting, setReconnecting] = useState(false);
    const reconnectCleanupRef = useRef<(() => void) | null>(null);
    useEffect(() => () => { reconnectCleanupRef.current?.(); }, []);

    const handleReconnect = async () => {
        if (!source || !sourceId) return;
        const provider = AUTH_PROVIDERS[source.source_type];
        if (!provider) return;

        setReconnecting(true);
        reconnectCleanupRef.current?.();
        try {
            const sep = provider.loginPath.includes('?') ? '&' : '?';
            const res = await authFetch(`${provider.loginPath}${sep}redirect_origin=${encodeURIComponent(window.location.origin)}`);
            if (!res.ok) throw new Error('Failed to get auth URL');
            const { auth_url } = await res.json();

            reconnectCleanupRef.current = runOAuthPopup({
                url: auth_url,
                name: provider.popupName,
                storageKey: provider.storageKey,
                messageType: provider.messageType,
                onResult: async (data) => {
                    reconnectCleanupRef.current = null;
                    setReconnecting(false);
                    if (data?.type === `${provider.messageType}-error`) {
                        alert(data.error || `Signing in to ${provider.label} failed. Please try again.`);
                        return;
                    }
                    if (data?.type !== provider.messageType || !data.credentials) return;
                    try {
                        const saved = await authFetch(`/sources/${sourceId}/reconnect`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ credentials: data.credentials }),
                        });
                        if (!saved.ok) {
                            const body = await saved.json().catch(() => null);
                            throw new Error(body?.detail || 'Failed to reconnect. Please try again.');
                        }
                        alert(`${provider.label} reconnected.`);
                    } catch (err) {
                        alert(err instanceof Error ? err.message : 'Failed to reconnect. Please try again.');
                    }
                },
            });
        } catch (err) {
            setReconnecting(false);
            alert('Failed to reconnect. Please try again.');
        }
    };

    const handleEditSuccess = async () => {
        setIsEditing(false);
        if (!sourceId) return;

        try {
            const sourceRes = await authFetch(`/sources/${sourceId}`);
            if (!sourceRes.ok) throw new Error('Failed to reload connection');
            const sourceData = await sourceRes.json();
            setSource(sourceData);
        } catch (err) {
            console.error('Failed to reload source:', err);
        }
    };


    if (loading) return <PageLoader label="Loading source..." />;
    if (error || !source) return <PageError title="Failed to load source" message={error || 'Connection not found'} />;

    // Navaris holds what people upload and what Navaris writes: nothing to sync,
    // configure or match against document types.
    const builtin = isBuiltinSource(source.source_type);
    const tabs = builtin
        ? [{ id: 'files' as TabType, label: 'Files', icon: FileText }]
        : [
            { id: 'files' as TabType, label: 'Files', icon: FileText },
            { id: 'review' as TabType, label: 'Candidates', icon: Sparkles },
            ...(source.searchable ? [{ id: 'searches' as TabType, label: 'Searches', icon: Search }] : []),
            { id: 'jobs' as TabType, label: 'Jobs', icon: Logs },
        ];

    const configMetadata: Record<string, any> = {};
    for (const [key, value] of Object.entries(source.config || {})) {
        if (key === 'owner_user_id') continue;
        const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        configMetadata[label] = value;
    }

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey flex flex-wrap items-start justify-between gap-6">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="navaris-tile-tint w-12 h-12 rounded-xl flex border border-softgrey p-2 items-center justify-center shrink-0">
                        {jobs.some(j => j.status === 'pending' || j.status === 'queued' || j.status === 'processing') ? (
                            <Loader className="w-7 h-7 animate-spin text-brand" />
                        ) : (
                            <SourceLogo type={source.source_type} size="xl" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-graphite">{builtin ? sourceLabel(source.source_type) : source.source}</h3>
                            {!builtin && <MetadataInfo metadata={configMetadata} />}
                        </div>
                        <div className="flex flex-wrap items-center gap-5 mt-1 text-sm text-coolslate">
                            <span className="inline-flex items-center gap-1.5"><FolderClock className="w-4 h-4 text-brand" />{formatDateTime(source.created_at)}</span>
                            <span className="inline-flex items-center gap-1.5"><FolderSync className="w-4 h-4 text-brand" />{formatDateTime(source.updated_at)}</span>
                            <span className="inline-flex items-center gap-1.5"><Files className="w-4 h-4 text-brand" />{source.file_count}</span>
                            {source.sync_schedule !== 'off' && source.next_sync_at && (
                                <span className="inline-flex items-center gap-1.5" title="Next scheduled sync">
                                    <RefreshCw className="w-4 h-4 text-brand" />
                                    Next: {formatDateTime(source.next_sync_at, false)}
                                </span>
                            )}
                        </div>
                        {builtin && (
                            <p className="text-sm text-coolslate mt-2">
                                Files uploaded to a document type are under Documents, with the documents Navaris generates. Files uploaded into a chat, and documents Navaris writes there, are under Chats, and stay yours alone until you file them under a document type.
                            </p>
                        )}
                    </div>
                </div>
                {!builtin && <SourceActionsMenu
                    isEditing={isEditing}
                    onEdit={() => setIsEditing(p => !p)}
                    onReconnect={isOAuthSource ? handleReconnect : undefined}
                    reconnecting={reconnecting}
                    schedule={source.sync_schedule ?? 'off'}
                    onScheduleChange={handleScheduleChange}
                    onSyncNow={handleRefreshSource}
                    syncing={refreshingSource}
                    syncs={!source.live}
                />}
            </div>

            {isEditing ? (
                <SourceForm editSource={source} onSuccess={handleEditSuccess} />
            ) : source.live ? (
                <p className="text-sm text-coolslate">
                    Navaris searches {sourceLabel(source.source_type)} as the person who connected it, only while they ask Navaris something, and only they see what it finds. It keeps nothing it reads there: when a conversation settles something worth keeping, Navaris asks before recording it as theirs.
                </p>
            ) : (
            <>
            <Tabs items={tabs} active={activeTab} onChange={setActiveTab} />

            <div>
                    {activeTab === 'files' && (
                        <div>
                            {!builtin && <DismissableInfoCard
                                storageKey="sourceBrowser.introDismissed"
                                title="Browsing this connection"
                                icon={Files}
                            >
                                This is a live view of the files and folders in this connection —
                                we've indexed their names so you can find documents. Navaris files the ones
                                that match a kind of document it is looking for, and you can link any other
                                file or folder yourself. Click into folders to
                                browse, or <strong>search by name</strong> to jump to any file or folder.
                                A <strong>Linked</strong> badge marks what's already linked to a document, and
                                a folder shows a count of the linked items nested inside it.
                            </DismissableInfoCard>}
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coolslate/50" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search files..."
                                    className="w-full pl-9 pr-9 py-2 border border-softgrey rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-coolslate/50 hover:text-coolslate"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {isSearchActive ? (
                                searchLoading ? (
                                    <div className="flex justify-center py-8">
                                        <Loader className="w-8 h-8 animate-spin text-brand" />
                                    </div>
                                ) : searchResults.folders.length === 0 && searchResults.files.length === 0 ? (
                                    <p className="text-center text-coolslate py-8">No matches for your search</p>
                                ) : (
                                    <div className="space-y-0.5">
                                        {searchResults.folders.map(folder => {
                                            const segments = folder.path.split('/').filter(Boolean);
                                            return (
                                                <FolderRow
                                                    key={folder.path}
                                                    name={folder.name}
                                                    subtitle={segments.length > 1 ? segments.slice(0, -1).join(' / ') : undefined}
                                                    linked={folder.linked}
                                                    linkedCount={folder.linked_count}
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        handleNavigate(segments.map(name => ({ id: name, name })));
                                                    }}
                                                />
                                            );
                                        })}
                                        {searchResults.files.map(doc => (
                                            <FileRow key={doc.id} file={doc} showFolder sourceId={sourceId} linkQuery={linkQuery} linked={doc.linked} />
                                        ))}
                                        <div ref={sentinelRef} className="py-2">
                                            {loadingMore && (
                                                <div className="flex justify-center">
                                                    <Loader className="w-5 h-5 animate-spin text-brand" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            ) : searchQuery.trim().length > 0 ? (
                                <p className="text-center text-coolslate py-8">Type at least 3 characters to search</p>
                            ) : (
                                <SourceBrowser
                                    folders={browseFolders}
                                    path={browsePath}
                                    files={browseFiles.map(file => ({ file, linkQuery, linked: file.linked }))}
                                    loading={browseLoading}
                                    error={browseError}
                                    emptyMessage="No files found"
                                    maxHeight="max-h-96"
                                    onNavigate={handleNavigate}
                                    headerAction={
                                        builtin || browsePath.length === 0 ? undefined :
                                        currentFolderLinked ? (
                                            <Link
                                                to={`/connections/${sourceId}/folder?path=${encodeURIComponent(browsePath.map(f => f.name).join('/'))}${typeParam ? `&type=${encodeURIComponent(typeParam)}` : ''}`}
                                                className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-state-success bg-state-success/15 border border-state-success/30 rounded-md hover:bg-state-success/25 transition-colors"
                                                title="This folder is linked — view or unlink"
                                            >
                                                <Link2 className="w-3 h-3" />
                                                Linked
                                            </Link>
                                        ) : (
                                            <Link
                                                to={`/connections/${sourceId}/folder?path=${encodeURIComponent(browsePath.map(f => f.name).join('/'))}${typeParam ? `&type=${encodeURIComponent(typeParam)}` : ''}`}
                                                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-brand hover:text-brand-dark hover:bg-offwhite rounded-md transition-colors"
                                                title="Link this folder to a document type"
                                            >
                                                <FolderSymlink className="w-3.5 h-3.5" />
                                                Link folder
                                            </Link>
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}

                    {activeTab === 'jobs' && !builtin && (
                        <div>
                            {loadingJobs ? (
                                <div className="flex justify-center py-8">
                                    <Loader className="w-8 h-8 animate-spin text-brand" />
                                </div>
                            ) : jobs.length === 0 ? (
                                <p className="text-center text-coolslate py-8">No jobs found</p>
                            ) : (
                                <div className="space-y-2">
                                    {jobs.map((job) => (
                                        <Link
                                            key={job.id}
                                            to={`/jobs/${job.id}`}
                                            className="p-4 bg-white border border-softgrey rounded-lg hover:border-brand hover:bg-brand/5 transition-colors cursor-pointer block"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${jobStatusBgColor(job.status)}`}>
                                                    <JobStatusIcon status={job.status} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-medium text-graphite">
                                                            {job.task_type ? titleCaseSnake(job.task_type) : `Job ${job.id.substring(0, 8)}`}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-1 text-xs text-coolslate">
                                                        {job.created_at && (
                                                            <span>{formatDateTime(job.created_at)}</span>
                                                        )}
                                                    </div>
                                                    {job.error && (
                                                        <p className="text-xs text-state-error mt-2">{job.error}</p>
                                                    )}
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-coolslate/70" />
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'review' && !builtin && (
                        <SourceReviewTab sourceId={sourceId!} onLinked={() => loadSourceDetails(true)} />
                    )}

                    {activeTab === 'searches' && !builtin && source.searchable && (
                        <SourceQueriesTab sourceId={sourceId!} />
                    )}
            </div>
            </>
            )}
        </div>
    );
}
